import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { clearAuth, loginSuccess, setLoading } from "@/redux/auth/authSlice";
import { authService } from "@/services/authService";
import { tokenStorage } from "@/utils/tokenStorage";

export default function AuthInitializer() {
  const dispatch = useDispatch();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations.
    // Set synchronously so React StrictMode's second effect call sees it immediately
    // and exits early — preventing the double-invoke stuck-loading bug.
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initializeAuth = async () => {
      try {
        dispatch(setLoading(true));

        // Check for existing token
        const token = tokenStorage.getToken();

        if (!token) {
          dispatch(clearAuth());
          return;
        }

        // Token exists; validate with timeout to prevent hanging
        const validationPromise = authService.validateToken();
        const timeoutPromise = new Promise<null>(
          (resolve) => setTimeout(() => resolve(null), 8000), // 8 second timeout
        );

        const user = await Promise.race([validationPromise, timeoutPromise]);

        if (user) {
          dispatch(loginSuccess({ token, user }));
        } else {
          // Token invalid or validation timed out
          dispatch(clearAuth());
          tokenStorage.removeToken();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        dispatch(clearAuth());
      } finally {
        // Always dispatch — Redux store is global and safe to dispatch to at any time
        dispatch(setLoading(false));
      }
    };

    initializeAuth();
  }, [dispatch]);

  return null; // This component doesn't render anything
}
